(function () {
  'use strict';

  if (customElements.get('wolf-stats-counter')) return;

  const RING_RADIUS = 54;
  const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

  function easeOutQuad(t) {
    return t * (2 - t);
  }

  function formatValue(value, hasDecimal) {
    return hasDecimal ? value.toFixed(1) : String(Math.floor(value));
  }

  class WolfStatsCounter extends HTMLElement {
    connectedCallback() {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.numbers = Array.from(this.querySelectorAll('.stats-counter__number[data-count-to]'));
      this.ringTexts = Array.from(this.querySelectorAll('.stats-counter__progress-text[data-count-to]'));
      this.rings = Array.from(this.querySelectorAll('.stats-counter__progress-ring[data-count-to]'));
      this.animated = false;

      this.addEventListener('click', this.onClick.bind(this));

      if (this.reducedMotion.matches) {
        // Degrade gracefully: markup already contains the final numbers,
        // just set the rings to their final position and pause any videos.
        this.setRingsFinal();
        this.pauseVideos();
        return;
      }

      if (!('IntersectionObserver' in window) || (this.numbers.length === 0 && this.ringTexts.length === 0)) {
        this.setRingsFinal();
        return;
      }

      // Reset visible values to their starting point before animating.
      this.numbers.forEach((el) => {
        el.textContent = (el.dataset.prefix || '') + '0' + (el.dataset.suffix || '');
      });
      this.ringTexts.forEach((el) => {
        el.textContent = (el.dataset.prefix || '') + '0' + (el.dataset.suffix || '');
      });

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.startAnimation();
              this.observer.disconnect();
            }
          });
        },
        { threshold: 0.3 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    onClick(event) {
      const trigger = event.target.closest('[data-scroll-target]');
      if (!trigger) return;
      const targetClass = trigger.getAttribute('data-scroll-target');
      if (!targetClass) return;
      let target = null;
      try {
        target = document.querySelector('.' + CSS.escape(targetClass));
      } catch (error) {
        target = null;
      }
      if (target) {
        event.preventDefault();
        target.scrollIntoView({
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
          block: 'start'
        });
      }
    }

    pauseVideos() {
      this.querySelectorAll('video[autoplay]').forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
    }

    ringFinalOffset(ring) {
      const override = ring.dataset.overrideProgress;
      const target = parseFloat(ring.dataset.countTo) || 0;
      const pct = Math.min(override && override !== 'false' ? parseFloat(override) : target, 100);
      return RING_CIRCUMFERENCE - (pct / 100) * RING_CIRCUMFERENCE;
    }

    setRingsFinal() {
      this.rings.forEach((ring) => {
        ring.style.strokeDashoffset = this.ringFinalOffset(ring);
      });
    }

    animateCount(element) {
      const target = parseFloat(element.dataset.countTo);
      if (isNaN(target)) return;
      const duration = parseInt(element.dataset.duration, 10) || 2000;
      const prefix = element.dataset.prefix || '';
      const suffix = element.dataset.suffix || '';
      const hasDecimal = target % 1 !== 0;
      const startTime = performance.now();

      const update = (currentTime) => {
        const progress = Math.min((currentTime - startTime) / duration, 1);
        const current = easeOutQuad(progress) * target;
        element.textContent = prefix + formatValue(current, hasDecimal) + suffix;
        if (progress < 1) {
          requestAnimationFrame(update);
        } else {
          element.textContent = prefix + formatValue(target, hasDecimal) + suffix;
        }
      };
      requestAnimationFrame(update);
    }

    animateRing(textElement, ring) {
      const target = parseFloat(textElement.dataset.countTo);
      if (isNaN(target)) return;
      const duration = parseInt(textElement.dataset.duration, 10) || 2000;
      const prefix = textElement.dataset.prefix || '';
      const suffix = textElement.dataset.suffix || '';
      const hasDecimal = target % 1 !== 0;
      const override = ring.dataset.overrideProgress;
      const manualProgress = override && override !== 'false' ? parseFloat(override) : null;
      const startTime = performance.now();

      const update = (currentTime) => {
        const progress = Math.min((currentTime - startTime) / duration, 1);
        const eased = easeOutQuad(progress);
        const current = eased * target;
        textElement.textContent = prefix + formatValue(current, hasDecimal) + suffix;

        const percentage = Math.min(manualProgress !== null ? manualProgress * eased : current, 100);
        ring.style.strokeDashoffset = RING_CIRCUMFERENCE - (percentage / 100) * RING_CIRCUMFERENCE;

        if (progress < 1) {
          requestAnimationFrame(update);
        } else {
          textElement.textContent = prefix + formatValue(target, hasDecimal) + suffix;
          ring.style.strokeDashoffset = this.ringFinalOffset(ring);
        }
      };
      requestAnimationFrame(update);
    }

    startAnimation() {
      if (this.animated) return;
      this.animated = true;
      this.numbers.forEach((el) => this.animateCount(el));
      this.ringTexts.forEach((textElement, index) => {
        if (this.rings[index]) this.animateRing(textElement, this.rings[index]);
      });
    }
  }

  customElements.define('wolf-stats-counter', WolfStatsCounter);
})();
